import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { changePasswordSchema, type MeDTO } from '@lightbm/shared';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { useErrorText } from '@/i18n/messages';
import { applyServerErrors } from '@/lib/forms';
import { authApi } from '../api';
import { useAuth } from '../auth-context';
import { PasswordInput } from './PasswordInput';

const changePasswordForm = changePasswordSchema
  .and(z.object({ confirm: z.string() }))
  .refine((data) => data.newPassword === data.confirm, {
    path: ['confirm'],
    message: 'validation.passwordsDiffer',
  });
type ChangePasswordForm = z.infer<typeof changePasswordForm>;

/** USR-10: change one's own password (also the forced first sign-in step). */
export function ChangePasswordForm({
  onChanged,
  submitLabel,
}: {
  onChanged?: (me: MeDTO) => void;
  submitLabel?: string;
}) {
  const { t } = useTranslation('auth');
  const { setUser } = useAuth();
  const errorText = useErrorText();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordForm),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });

  const onSubmit = handleSubmit(async ({ currentPassword, newPassword }) => {
    try {
      const me = await authApi.changePassword({ currentPassword, newPassword });
      setUser(me);
      reset();
      toast.success(t('change.done'));
      onChanged?.(me);
    } catch (error) {
      if (!applyServerErrors(error, setError)) toast.error(errorText(error));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <Field label={t('fields.currentPassword')} error={errors.currentPassword}>
        <PasswordInput {...register('currentPassword')} autoComplete="current-password" />
      </Field>
      <Field label={t('fields.newPassword')} error={errors.newPassword} hint={t('password.rule')}>
        <PasswordInput {...register('newPassword')} autoComplete="new-password" />
      </Field>
      <Field label={t('fields.confirmPassword')} error={errors.confirm}>
        <PasswordInput {...register('confirm')} autoComplete="new-password" />
      </Field>
      <Button type="submit" loading={isSubmitting}>
        {submitLabel ?? t('change.submit')}
      </Button>
    </form>
  );
}
